import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Save, User, Mail, Bell, Shield, Key, Trash2 } from "lucide-react";

const SettingsPage = () => {
	const { currentUser, isAdmin } = useAuth();
	const [settings, setSettings] = useState({
		name: currentUser?.name || "",
		email: currentUser?.email || "",
		notifications: true,
		emailUpdates: false,
		weeklyDigest: true,
	});
	const [saved, setSaved] = useState(false);

	const handleSave = () => {
		// Save settings logic here
		setSaved(true);
		setTimeout(() => setSaved(false), 3000);
	};

	return (
		<div className="space-y-6 max-w-5xl">
			{/* Header */}
			<div className="relative">
				<div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-br from-orange-700/20 to-orange-500/20 rounded-full blur-3xl"></div>
				<div className="relative">
					<h2 className="text-3xl font-bold text-white mb-2">Settings</h2>
					<p className="text-gray-400">
						Manage your account preferences and security.
					</p>
				</div>
			</div>

			{/* Profile Information Card */}
			<div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors">
				<div className="bg-gradient-to-r from-orange-700/10 to-orange-500/10 px-6 py-4 border-b border-gray-700">
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-orange-600/20 rounded-lg">
							<User className="w-5 h-5 text-orange-400" />
						</div>
						<div>
							<h3 className="text-lg font-semibold text-white">
								Profile Information
							</h3>
							<p className="text-sm text-gray-400">
								Update your personal details
							</p>
						</div>
					</div>
				</div>
				<div className="p-6 space-y-5">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
						<div>
							<label className="flex items-center space-x-2 text-sm font-medium mb-2 text-gray-300">
								<User className="w-4 h-4 text-gray-400" />
								<span>Full Name</span>
							</label>
							<input
								type="text"
								value={settings.name}
								onChange={(e) =>
									setSettings({ ...settings, name: e.target.value })
								}
								className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
								placeholder="Enter your name"
							/>
						</div>
						<div>
							<label className="flex items-center space-x-2 text-sm font-medium mb-2 text-gray-300">
								<Mail className="w-4 h-4 text-gray-400" />
								<span>Email Address</span>
							</label>
							<input
								type="email"
								value={settings.email}
								onChange={(e) =>
									setSettings({ ...settings, email: e.target.value })
								}
								className="w-full px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
								placeholder="your@email.com"
							/>
						</div>
					</div>
					{isAdmin && (
						<div className="mt-4 p-3 bg-orange-900/20 border border-orange-700/50 rounded-lg flex items-center space-x-2">
							<Shield className="w-4 h-4 text-orange-400" />
							<span className="text-sm text-orange-300">
								Administrator Account
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Notifications Card */}
			<div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors">
				<div className="bg-gradient-to-r from-blue-700/10 to-blue-500/10 px-6 py-4 border-b border-gray-700">
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-blue-600/20 rounded-lg">
							<Bell className="w-5 h-5 text-blue-400" />
						</div>
						<div>
							<h3 className="text-lg font-semibold text-white">
								Notification Preferences
							</h3>
							<p className="text-sm text-gray-400">
								Choose how you want to be notified
							</p>
						</div>
					</div>
				</div>
				<div className="p-6 space-y-4">
					{/* Toggle Item */}
					<div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-lg hover:bg-gray-900/50 transition-colors">
						<div className="flex-1">
							<p className="text-white font-medium flex items-center space-x-2">
								<span>Push Notifications</span>
							</p>
							<p className="text-sm text-gray-400 mt-1">
								Get notified about new content and updates
							</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								checked={settings.notifications}
								onChange={(e) =>
									setSettings({ ...settings, notifications: e.target.checked })
								}
								className="sr-only peer"
							/>
							<div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-orange-700 peer-checked:to-orange-500"></div>
						</label>
					</div>

					<div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-lg hover:bg-gray-900/50 transition-colors">
						<div className="flex-1">
							<p className="text-white font-medium">Email Updates</p>
							<p className="text-sm text-gray-400 mt-1">
								Receive important updates via email
							</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								checked={settings.emailUpdates}
								onChange={(e) =>
									setSettings({ ...settings, emailUpdates: e.target.checked })
								}
								className="sr-only peer"
							/>
							<div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-orange-700 peer-checked:to-orange-500"></div>
						</label>
					</div>

					<div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-lg hover:bg-gray-900/50 transition-colors">
						<div className="flex-1">
							<p className="text-white font-medium">Weekly Digest</p>
							<p className="text-sm text-gray-400 mt-1">
								Get a weekly summary of activity
							</p>
						</div>
						<label className="relative inline-flex items-center cursor-pointer">
							<input
								type="checkbox"
								checked={settings.weeklyDigest}
								onChange={(e) =>
									setSettings({ ...settings, weeklyDigest: e.target.checked })
								}
								className="sr-only peer"
							/>
							<div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-orange-700 peer-checked:to-orange-500"></div>
						</label>
					</div>
				</div>
			</div>

			{/* Security Card */}
			<div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors">
				<div className="bg-gradient-to-r from-purple-700/10 to-purple-500/10 px-6 py-4 border-b border-gray-700">
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-purple-600/20 rounded-lg">
							<Key className="w-5 h-5 text-purple-400" />
						</div>
						<div>
							<h3 className="text-lg font-semibold text-white">
								Security & Privacy
							</h3>
							<p className="text-sm text-gray-400">
								Manage your account security
							</p>
						</div>
					</div>
				</div>
				<div className="p-6 space-y-4">
					<button className="w-full md:w-auto px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all flex items-center justify-center space-x-2 group">
						<Key className="w-4 h-4 group-hover:rotate-12 transition-transform" />
						<span>Change Password</span>
					</button>
					<button className="w-full md:w-auto px-6 py-3 bg-red-900/20 hover:bg-red-900/40 text-red-400 border border-red-800/50 hover:border-red-700 rounded-lg transition-all flex items-center justify-center space-x-2 group">
						<Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
						<span>Delete Account</span>
					</button>
				</div>
			</div>

			{/* Save Button */}
			<div className="flex items-center justify-between pt-4">
				<div>
					{saved && (
						<p className="text-green-400 text-sm flex items-center space-x-2">
							<span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
							<span>Settings saved successfully!</span>
						</p>
					)}
				</div>
				<button
					onClick={handleSave}
					className="px-8 py-3 bg-gradient-to-r from-orange-700 to-orange-500 text-white rounded-lg hover:from-orange-800 hover:to-orange-600 transition-all flex items-center space-x-2 shadow-lg shadow-orange-900/50 hover:shadow-orange-900/70 group"
				>
					<Save className="w-5 h-5 group-hover:scale-110 transition-transform" />
					<span className="font-medium">Save Changes</span>
				</button>
			</div>
		</div>
	);
};

export default SettingsPage;
